import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { useHasPermission } from '@hooks/has-permission/useHasPermission';
import { AppRolesEnum } from '@features/routing/appRolesEnum';
import { NavigateRoutesEnum } from '@features/routing/routesEnum';
import { StatisticModel } from '@pages/preview/cards-area/StatisticModel';
import { selectedDateWithoutTime } from '@store/global/selectors';
import { setOrdersFilter } from '@pages/orders/store/slice';
import { setFilterDate, setDateRange as setRedatedDates } from '@pages/reporting-pages/redated-orders/store/slice';
import { setOrderPhase, setDateRange as setReducedDates } from '@pages/reporting-pages/reduced-orders/store/slice';
import { TileFilter } from '@api/shipping-data/types/TileFilter';
import { RedatedTypeEnum } from '@api/order-reporting/types/RedatedParams';
import { OrderPhaseEnum } from '@api/order-reporting/types/ReducedParams';
import StatisticCard from 'components/statistic-card/StatisticCard';
import { DateRange } from '@project-types/DateRange';



interface CardsAreaProps {
	statisticModel: StatisticModel,
}


const CardsArea = (props: CardsAreaProps) => {
	const { statisticModel } = props;
	const { totalOrders, redated, outgoingRedated, cancelledPlanning, reducedPlanning, changedPlanning,
		toBeDelivered, delivered, beingDelivered, remaining,
		perfectlyDelivered, notDelivered, reducedExecution, notPerfectlyDelivered,
		deliveredOnTime, deliveredOnSmsTime, deliveredInTimeSlot, departures } = props.statisticModel;

		const dateWithoutTime = useSelector(selectedDateWithoutTime);
		const navigate = useNavigate();
		const hasPermission = useHasPermission();
		const dispatch = useDispatch();
		const updateRedatedDates = (dateRange: DateRange) => dispatch(setRedatedDates(dateRange));
		const updateReducedDates = (dateRange: DateRange) => dispatch(setReducedDates(dateRange));
		const updateRedatedType = (value: RedatedTypeEnum) => dispatch(setFilterDate(value));
		const updateOrdersFilter = (filter: TileFilter) => dispatch(setOrdersFilter(filter));
		const updateOrdersOrderPhase = (orderPhase: string) => dispatch(setOrderPhase(orderPhase));

	const getRefreshTime = useMemo(() =>
		format(new Date(), 'HH:mm:ss'),
	[ statisticModel ]);


	return (
		<div className='cards-area'>
			
			<StatisticCard data={totalOrders}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					navigate(NavigateRoutesEnum.orders);
				}} />

			<StatisticCard data={redated}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.redatedOrdersReport_Read)) return;
					updateRedatedDates({ from: dateWithoutTime, to: dateWithoutTime });
					updateRedatedType(RedatedTypeEnum.incoming);
					navigate(NavigateRoutesEnum.reportingRedatedOrders);
				}} />

			<StatisticCard data={outgoingRedated}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.redatedOrdersReport_Read)) return;
					updateRedatedDates({ from: dateWithoutTime, to: dateWithoutTime });
					updateRedatedType(RedatedTypeEnum.outgoing);
					navigate(NavigateRoutesEnum.reportingRedatedOrders);
				}} />

			<StatisticCard data={cancelledPlanning}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.cancelledBeforeDelivery);
					navigate(NavigateRoutesEnum.orders);
				}} />

			<StatisticCard data={reducedPlanning}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.reducedOrdersReport_Read)) return;
					updateReducedDates({ from: dateWithoutTime, to: dateWithoutTime });
					updateOrdersOrderPhase(String(OrderPhaseEnum.planning));
					navigate(NavigateRoutesEnum.reportingReducedOrders);
				}} />

			<StatisticCard data={changedPlanning}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.allChangesBeforeDelivery);
					navigate(NavigateRoutesEnum.orders);
				}} />
			


			<StatisticCard data={toBeDelivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.totalToBeDelivered);
					navigate(NavigateRoutesEnum.orders);
				}} />
			
			<StatisticCard data={delivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.delivered);
					navigate(NavigateRoutesEnum.orders);
				}} />
			
			<StatisticCard data={beingDelivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.onWay);
					navigate(NavigateRoutesEnum.orders);
				}} />
			
			<StatisticCard data={remaining}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.remainingAtDc);
					navigate(NavigateRoutesEnum.orders);
				}} />
			
			<div className='empty-card-placeholder'></div>
			<div className='empty-card-placeholder'></div>
			


			<StatisticCard data={perfectlyDelivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.perfectlyDelivered);
					navigate(NavigateRoutesEnum.orders);
				}} />
			
			<StatisticCard data={notDelivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.notDelivered);
					navigate(NavigateRoutesEnum.orders);
				}} />

			<StatisticCard data={reducedExecution}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.reducedOrdersReport_Read)) return;
					updateReducedDates({ from: dateWithoutTime, to: dateWithoutTime });
					updateOrdersOrderPhase(String(OrderPhaseEnum.execution));
					navigate(NavigateRoutesEnum.reportingReducedOrders);
				}} />

			<StatisticCard data={notPerfectlyDelivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.imperfectOrdersTotal);
					navigate(NavigateRoutesEnum.orders);
				}} />
			<div className='empty-card-placeholder'></div>
			<div className='empty-card-placeholder'></div>


			<StatisticCard data={deliveredOnTime}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.deliveredOnTime);
					navigate(NavigateRoutesEnum.orders);
				}} />
			
			<StatisticCard data={deliveredOnSmsTime}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.deliveredOnSmsTime);
					navigate(NavigateRoutesEnum.orders);
				}} />

			<StatisticCard data={deliveredInTimeSlot}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.deliveredInDeliveryWindow);
					navigate(NavigateRoutesEnum.orders);
				}} />

			<StatisticCard data={departures}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.leftDepotOnTime);
					navigate(NavigateRoutesEnum.orders);
				}} />

			<p className='update-info'>Aktualizace proběhla: {getRefreshTime}</p>
			
		</div>
	);
}

export default CardsArea;